import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import type { FilterDefinition } from '@factorialco/f0-react/dist/experimental'

import {
  approvedAtFilter,
  createCategoryAsyncFilter,
  createCurrencyAsyncFilter,
  createEmployeeIdsAsyncFilter,
  createProjectAsyncFilter,
  createReimbursableCurrencyAsyncFilter,
  createSubcategoryAsyncFilter,
  creationTypeFilter,
  documentTypeFilter,
  internalReferenceFilter,
  reimbursableFilter,
  reportDateFilter,
  spendingAlertTypeFilter,
  statusFilter,
} from 'modules/expenses/components/expenses/DataCollection/filters'

import { reportedByFilter } from './_Filters/reportedByFilter'

interface UseMyExpensesFiltersProps {
  companyId: number
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  withinGroup?: boolean
  recommendationsEnabled?: boolean
}

export const useMyExpensesFilters = ({
  companyId,
  hasReportOnBehalfFeature,
  canCreateExpensesForOthers,
  withinGroup = false,
  recommendationsEnabled,
}: UseMyExpensesFiltersProps) => {
  const apolloClient = useApolloClient()

  return useMemo(() => {
    // Build filters in alphabetical order by user-facing label
    const filters: Record<string, FilterDefinition> = {
      spendingAlertType: spendingAlertTypeFilter(recommendationsEnabled), // alerts
      approvedAt: approvedAtFilter(), // approval date
      category: createCategoryAsyncFilter(apolloClient, companyId), // category
      reviewRequestAt: reportDateFilter(), // creation date
      currency: createCurrencyAsyncFilter(apolloClient, companyId), // currency
      documentType: documentTypeFilter(), // document type
      creationType: creationTypeFilter(), // expense type
      internalReference: internalReferenceFilter(), // internal reference
    }

    // owner
    if (hasReportOnBehalfFeature && canCreateExpensesForOthers && !withinGroup) {
      filters.employeeIds = createEmployeeIdsAsyncFilter(apolloClient, companyId)
    }

    filters.reimbursable = reimbursableFilter() // payment
    filters.project = createProjectAsyncFilter(apolloClient) // project
    filters.reimbursableCurrency = createReimbursableCurrencyAsyncFilter(apolloClient, companyId) // reimbursable currency

    // reported by
    if (hasReportOnBehalfFeature) {
      filters.reportedBy = reportedByFilter()
    }

    filters.status = statusFilter() // status
    filters.subcategory = createSubcategoryAsyncFilter(apolloClient, companyId) // subcategory

    return filters
  }, [
    apolloClient,
    companyId,
    hasReportOnBehalfFeature,
    canCreateExpensesForOthers,
    withinGroup,
    recommendationsEnabled,
  ])
}
